import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { calendarService } from '../../../services/calendarService';
import { projectService } from '../../../services/projectService';
import { contractorService } from '../../../services/contractorService';

const EventModal = ({ isOpen, onClose, onSuccess, event }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: 'availability',
    startDate: '',
    endDate: '',
    allDay: false,
    projectId: '',
    contractorId: '',
    teamMemberId: '',
    location: '',
    notes: ''
  });
  const [projects, setProjects] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRelatedData, setLoadingRelatedData] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadRelatedData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (event) {
      // Format dates for datetime-local input
      const formatDateForInput = (dateString) => {
        const date = new Date(dateString);
        return date?.toISOString()?.slice(0, 16);
      };

      setFormData({
        title: event?.title || '',
        description: event?.description || '',
        eventType: event?.eventType || 'availability',
        startDate: formatDateForInput(event?.startDate),
        endDate: formatDateForInput(event?.endDate),
        allDay: event?.allDay || false,
        projectId: event?.projectId || '',
        contractorId: event?.contractorId || '',
        teamMemberId: event?.teamMemberId || '',
        location: event?.location || '',
        notes: event?.notes || ''
      });
    } else {
      // Reset form for new event
      const now = new Date();
      const oneHourLater = new Date(now?.getTime() + 60 * 60 * 1000);
      
      setFormData({
        title: '',
        description: '',
        eventType: 'availability',
        startDate: now?.toISOString()?.slice(0, 16),
        endDate: oneHourLater?.toISOString()?.slice(0, 16),
        allDay: false,
        projectId: '',
        contractorId: '',
        teamMemberId: '',
        location: '',
        notes: ''
      });
    }
    setError('');
  }, [event, isOpen]);

  const loadRelatedData = async () => {
    setLoadingRelatedData(true);
    try {
      const [projectsData, contractorsData] = await Promise.all([
        projectService?.getAll(),
        contractorService?.getAll()
      ]);
      setProjects(projectsData || []);
      setContractors(contractorsData || []);
    } catch (error) {
      console.error('Error loading related data:', error);
    } finally {
      setLoadingRelatedData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (field) => (value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate dates
      if (new Date(formData?.startDate) >= new Date(formData?.endDate)) {
        setError('End date must be after start date');
        setLoading(false);
        return;
      }

      const eventData = {
        title: formData?.title,
        description: formData?.description,
        eventType: formData?.eventType,
        startDate: new Date(formData?.startDate)?.toISOString(),
        endDate: new Date(formData?.endDate)?.toISOString(),
        allDay: formData?.allDay,
        projectId: formData?.projectId || null,
        contractorId: formData?.contractorId || null,
        teamMemberId: formData?.teamMemberId || null,
        location: formData?.location,
        notes: formData?.notes
      };

      if (event) {
        await calendarService?.update(event?.id, eventData);
      } else {
        await calendarService?.create(eventData);
      }
      
      onSuccess?.();
    } catch (err) {
      setError(err?.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const eventTypeOptions = [
    { value: 'availability', label: 'Availability' },
    { value: 'booking', label: 'Booking' },
    { value: 'milestone', label: 'Milestone' },
    { value: 'inspection', label: 'Inspection' }
  ];

  const projectOptions = projects?.map(p => ({
    value: p?.id,
    label: `${p?.customerName} - ${p?.address}`
  }));

  const contractorOptions = contractors?.map(c => ({
    value: c?.id,
    label: `${c?.name} - ${c?.role || 'Contractor'}`
  }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {event ? 'Edit Event' : 'Create Event'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Title <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              name="title"
              value={formData?.title}
              onChange={handleChange}
              placeholder="Event title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Event description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Event Type
            </label>
            <Select
              name="eventType"
              value={formData?.eventType}
              onChange={handleSelectChange('eventType')}
              options={eventTypeOptions}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Start Date & Time <span className="text-destructive">*</span>
              </label>
              <Input
                type="datetime-local"
                name="startDate"
                value={formData?.startDate}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                End Date & Time <span className="text-destructive">*</span>
              </label>
              <Input
                type="datetime-local"
                name="endDate"
                value={formData?.endDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allDay"
              name="allDay"
              checked={formData?.allDay}
              onChange={handleChange}
              className="w-4 h-4 text-primary bg-background border-input rounded focus:ring-2 focus:ring-primary"
            />
            <label htmlFor="allDay" className="text-sm text-foreground">
              All day event
            </label>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Link to Records (Optional)</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Project
                </label>
                <Select
                  name="projectId"
                  value={formData?.projectId}
                  onChange={handleSelectChange('projectId')}
                  options={projectOptions}
                  placeholder={loadingRelatedData ? 'Loading projects...' : 'Select project (optional)'}
                  disabled={loadingRelatedData}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Link this event to a project for easy navigation
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Contractor
                </label>
                <Select
                  name="contractorId"
                  value={formData?.contractorId}
                  onChange={handleSelectChange('contractorId')}
                  options={contractorOptions}
                  placeholder={loadingRelatedData ? 'Loading contractors...' : 'Select contractor (optional)'}
                  disabled={loadingRelatedData}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Link this event to a contractor for easy navigation
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Location
            </label>
            <Input
              type="text"
              name="location"
              value={formData?.location}
              onChange={handleChange}
              placeholder="Event location"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Additional notes"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={loading || loadingRelatedData}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  {event ? 'Updating...' : 'Creating...'}
                </span>
              ) : (
                event ? 'Update Event' : 'Create Event'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;